import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Stack, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import UploadedFileIconElement from '../../../../components/Auth/Elements/UploadedFileIconElement';
import CompanyAboutPdfFile from '../../../../components/Company/CompanyAboutPdfFile/CompanyAboutPdfFile';
import CompanyAboutPhotoFile from '../../../../components/Company/CompanyAboutPhotoFile/CompanyAboutPhotoFile';
import CompanyAboutFilesIcon from '../../../../components/Icons/CompanyAboutFilesIcon';
import BaseModal from '../../../../components/MUIComponents/BaseModal';
import CustomButton from '../../../../components/MUIComponents/CustomButton';
import DragAndDropBox from '../../../../components/MUIComponents/DragAndDropBox/DragAndDropBox';
import SnackbarCustom from '../../../../components/MUIComponents/SnackbarCustom';
import TextField from '../../../../components/MUIComponents/TextField';
import { ButtonTypeEnum } from '../../../../enums/button-type-enum';
import { ElementIdEnum } from '../../../../enums/element-id-enum';
import { SnackbarSeverityEnum } from '../../../../enums/snackbar-severity-enum';
import useSnackbar from '../../../../hooks/useSnackBar';
import { Photo, Video } from '../../../../interfaces/media.interface';
import { useApplyJobpostMutation } from '../../../../store/apiSlice/jobpost/jobUserApplyApi';
import { errorHelper } from '../../../../utils/helper/error-helper';
import { isSupportedCompanyAboutFileHelper } from '../../../../utils/helper/file-validation-helper';
import { triggerFileInput } from '../../../../utils/helper/triggerInputHelper';
import { ApplyJobpostSchema } from '../../../../validation/apply-jobpost-validation';

const INTRODUCTION_MAX_LENGHT = 3000;

interface FileWithId {
  id: string;
  file: File;
}
interface ApplyJobpostFormInputs {
  jobpostId: string;
  introduction: string;
}

interface ApplyJobModalProps {
  isOpen?: boolean;
  onClose: () => void;
  jobpostId: string;
}

const ApplyJobModal = ({
  onClose,
  isOpen = false,
  jobpostId,
}: ApplyJobModalProps) => {
  const navigate = useNavigate();

  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const [_dragOver, setDragOver] = useState(false);
  const [media, setMedias] = useState<(Photo | Video)[]>([]);
  const [files, setFiles] = useState<FileWithId[]>([]);

  const mediaRef = useRef<(Photo | Video)[]>([]);

  useEffect(() => {
    mediaRef.current = media;
  }, [media]);

  useEffect(() => {
    return () => {
      mediaRef.current.forEach((m) => {
        if (m.fileUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(m.fileUrl);
        }
      });
    };
  }, []);

  const {
    control: _control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ApplyJobpostFormInputs>({
    resolver: yupResolver(ApplyJobpostSchema),
    defaultValues: {
      jobpostId,
    },
  });

  const addFile = async (files: FileList) => {
    const newFiles = Array.from(files);
    const invalidFiles = newFiles.filter(
      (file) => !isSupportedCompanyAboutFileHelper(file)
    );

    if (invalidFiles.length > 0) {
      return showSnackbar(
        'Invalid file type. Only images (JPEG, PNG, WebP) or PDF',
        SnackbarSeverityEnum.ERROR
      );
    }

    if (media.length + newFiles.length > 5) {
      return showSnackbar('Only 5 files allowed', SnackbarSeverityEnum.ERROR);
    }

    const mapedFiles: Photo[] = [];
    const filesWithIds: FileWithId[] = [];

    newFiles.forEach((file) => {
      const id = uuidv4();
      mapedFiles.push({
        id,
        fileUrl: URL.createObjectURL(file),
      });
      filesWithIds.push({
        id,
        file,
      });
    });

    setFiles((prev) => [...prev, ...filesWithIds]);
    setMedias((prev) => [...prev, ...mapedFiles]);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      addFile(event.target.files);
      event.target.value = '';
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    if (event.dataTransfer.files) {
      addFile(event.dataTransfer.files);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleFileRemove = (id: string) => {
    const mediaToRemove = media.find((m) => m.id === id);
    if (mediaToRemove?.fileUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(mediaToRemove.fileUrl);
    }
    setFiles((prevFiles) =>
      prevFiles.filter((fileWithId) => fileWithId.id !== id)
    );
    setMedias((prevMedias) => prevMedias.filter((media) => media.id !== id));
  };

  const handleCloseModal = () => {
    const urlsToRevoke = mediaRef.current
      .map((m) => m.fileUrl)
      .filter((fileUrl): fileUrl is string => !!fileUrl?.startsWith('blob:'));

    setFiles([]);
    setMedias([]);
    onClose();

    setTimeout(() => {
      urlsToRevoke.forEach((fileUrl) => URL.revokeObjectURL(fileUrl));
    }, 0);
  };

  const isPdfFile = (mediaFile: Photo | Video) => {
    const fileWithId = files.find((file) => file.id === mediaFile.id);

    return (
      mediaFile?.fileName?.toLowerCase()?.endsWith('.pdf') ||
      fileWithId?.file?.type === 'application/pdf'
    );
  };

  const [applyJobpost, { isLoading: isApplying }] = useApplyJobpostMutation();

  const handleApplyJobpost = async (data: FormData) => {
    try {
      console.info('DATA INSIDE', data);
      await applyJobpost(data).unwrap();

      showSnackbar(
        'Job was applied successfully',
        SnackbarSeverityEnum.SUCCESS
      );
      setTimeout(() => {
        handleCloseModal();
        navigate('/job-posts');
      }, 1000);
    } catch (error) {
      showSnackbar(errorHelper(error), SnackbarSeverityEnum.ERROR);
      console.error('Apply failed: ', error);
    }
  };

  const onSubmit = async (data: ApplyJobpostFormInputs) => {
    try {
      const formData = new FormData();
      data.jobpostId = jobpostId;

      formData.append('introduction', data.introduction);
      formData.append('jobpostId', data.jobpostId);

      if (files.length > 0) {
        files.forEach((fileWithId) => {
          formData.append(
            'documents',
            fileWithId.file,
            encodeURIComponent(fileWithId.file.name)
          );
        });
      }

      await handleApplyJobpost(formData);
    } catch (error) {
      console.error('Apply failed:', error);
    }
  };

  const modalTitle = useMemo(() => {
    return (
      <Typography
        variant="h2"
        textAlign="center"
        fontSize={'26px'}
        fontWeight={600}
      >
        Apply
      </Typography>
    );
  }, []);

  const modalFooter = useMemo(() => {
    return (
      <Box mt={3}>
        <Grid
          container
          justifyContent="space-between"
          flexDirection={{ xs: 'column-reverse', lg: 'row' }}
          gap={{ xs: '10px', lg: '0px' }}
          width={'100%'}
        >
          <Grid item>
            <CustomButton
              sx={{ width: { xs: '100%', lg: '220px' } }}
              fullWidth
              variant="outlined"
              variantType={ButtonTypeEnum.SECONDARY}
              onClick={handleCloseModal}
            >
              Cancel
            </CustomButton>
          </Grid>
          <Grid item>
            <CustomButton
              sx={{ width: { xs: '100%', lg: '220px' } }}
              fullWidth
              variant="contained"
              variantType={ButtonTypeEnum.PRIMARY}
              type="submit"
              onClick={handleSubmit(onSubmit)}
              disabled={isApplying}
            >
              Send
            </CustomButton>
          </Grid>
        </Grid>
      </Box>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleCloseModal, handleSubmit, isApplying]); // Intentionally omitting onSubmit - function recreated every render, would defeat useMemo purpose

  return (
    <BaseModal
      modalSx={{ borderColor: 'transparent !important' }}
      header={{ component: modalTitle }}
      disableEscapeButton
      isOpen={isOpen}
      toggle={handleCloseModal}
      footer={{ component: modalFooter }}
    >
      <Stack spacing="36px">
        <Box className="introduction-section">
          <Typography component="p" fontSize="12px" fontWeight={700} mb="16px">
            Introduction:
          </Typography>
          <Box className="introduction-container" position="relative">
            <TextField
              placeholder="Highlight your relevant skills and experiences"
              height={160}
              register={register('introduction')}
              error={!!errors.introduction}
              maxLength={INTRODUCTION_MAX_LENGHT}
              helperText={
                errors.introduction ? errors.introduction.message : ''
              }
            />
          </Box>
        </Box>

        <Box className="cover-letter-section">
          <Typography
            component={'p'}
            fontSize={'12px'}
            fontWeight={700}
            mb={'16px'}
          >
            Upload Resume and Cover Letter (optional):
          </Typography>

          <Box
            display={'flex'}
            flexDirection={'column'}
            className="add-media-container"
            position="relative"
            gap={'28px'}
          >
            <DragAndDropBox
              handleDragOver={handleDragOver}
              handleDragLeave={handleDragLeave}
              handleDrop={handleDrop}
              handleFileChange={handleFileChange}
              triggerFileInput={() =>
                triggerFileInput(ElementIdEnum.ADD_COMPANY_IMAGE_ABOUT_BUTTON)
              }
              fileInputId={ElementIdEnum.ADD_COMPANY_IMAGE_ABOUT_BUTTON}
              text={
                <>
                  Drag and drop your file here
                  <br />
                  Only one file allowed. Only images (JPEG, PNG, WebP) or PDF
                  are accepted.
                </>
              }
              icon={
                <UploadedFileIconElement>
                  <CompanyAboutFilesIcon active />
                </UploadedFileIconElement>
              }
            />
            {!!media.length &&
              media.map((file) =>
                isPdfFile(file) ? (
                  <CompanyAboutPdfFile
                    key={file.id}
                    files={[file]}
                    handleFileRemove={handleFileRemove}
                  />
                ) : (
                  <CompanyAboutPhotoFile
                    key={file.id}
                    files={[file]}
                    handleFileRemove={handleFileRemove}
                  />
                )
              )}
          </Box>
        </Box>
      </Stack>
      <SnackbarCustom
        open={snackbar.open}
        onClose={closeSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </BaseModal>
  );
};

export default ApplyJobModal;
